/**
 * ChatWindow Component
 *
 * Main chat interface combining message list and input
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Settings,
  Download,
  Trash2,
  MoreVertical,
  AlertCircle,
  Loader2,
  Bot,
} from 'lucide-react';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import {
  useACFSession,
  useACFMessages,
  useSendACFMessage,
  useACFStream,
  useExportACFConversation,
} from '@/hooks/useACF';
import { useToast } from '@/shared/hooks/useToast';

interface ChatWindowProps {
  sessionId: string;
  onSettingsClick?: () => void;
  onDelete?: () => void;
  className?: string;
}

/**
 * ChatWindow - Main chat interface
 */
export function ChatWindow({
  sessionId,
  onSettingsClick,
  onDelete,
  className,
}: ChatWindowProps): React.JSX.Element {
  const { toast } = useToast();

  // Fetch session and messages
  const {
    data: session,
    isLoading: sessionLoading,
    error: sessionError,
  } = useACFSession(sessionId);

  const {
    data: messages = [],
    isLoading: messagesLoading,
    error: messagesError,
  } = useACFMessages(sessionId);

  // Mutations
  const sendMessage = useSendACFMessage();
  const exportConversation = useExportACFConversation();

  // Streaming
  const {
    isStreaming,
    streamContent,
    error: streamError,
    startStream,
    stopStream,
  } = useACFStream();

  // Handle sending message
  const handleSend = React.useCallback(
    async (content: string) => {
      try {
        // For now, use non-streaming approach since stream endpoint may not exist
        // First send user message
        await sendMessage.mutateAsync({
          sessionId,
          message: {
            role: 'user',
            content,
          },
        });

        // Then get AI response (would use streaming in production)
        // For now, we'll simulate by sending to the regular endpoint
        // and the backend will handle generating the response

        // Try streaming first
        try {
          await startStream(sessionId, content, {
            model: session?.settings.model,
            onComplete: () => {
              toast({
                title: 'Response complete',
                description: 'AI response received successfully',
              });
            },
            onError: (error) => {
              // If streaming fails, fall back to showing the sent message
              console.warn('Streaming not available:', error);
            },
          });
        } catch {
          // Streaming endpoint may not exist, which is fine
          // The message was already sent
        }
      } catch (error) {
        toast({
          title: 'Send Failed',
          description: error instanceof Error ? error.message : 'Failed to send message',
          variant: 'destructive',
        });
      }
    },
    [sessionId, session?.settings.model, sendMessage, startStream, toast]
  );

  // Handle export
  const handleExport = React.useCallback(
    async (format: 'json' | 'markdown') => {
      try {
        const result = await exportConversation.mutateAsync({
          sessionId,
          format,
        });

        // Download the content
        const blob = new Blob([result.conversation], {
          type: format === 'json' ? 'application/json' : 'text/markdown',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `conversation-${sessionId}.${format === 'json' ? 'json' : 'md'}`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast({
          title: 'Export Successful',
          description: `Conversation exported as ${format.toUpperCase()}`,
        });
      } catch (error) {
        toast({
          title: 'Export Failed',
          description: error instanceof Error ? error.message : 'Failed to export',
          variant: 'destructive',
        });
      }
    },
    [sessionId, exportConversation, toast]
  );

  // Loading state
  if (sessionLoading || messagesLoading) {
    return (
      <Card className={cn('flex flex-col h-full', className)}>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading conversation...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (sessionError || messagesError) {
    return (
      <Card className={cn('flex flex-col h-full border-destructive', className)}>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div>
              <p className="font-medium text-destructive">Failed to load conversation</p>
              <p className="text-sm text-muted-foreground">
                {sessionError instanceof Error ? sessionError.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Not found state
  if (!session) {
    return (
      <Card className={cn('flex flex-col h-full', className)}>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center">
            <Bot className="h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">Session not found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <CardHeader className="border-b shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot className="h-5 w-5 text-primary" />
            <div>
              <CardTitle className="text-lg">
                {session.title || 'AI Coding Session'}
              </CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge
                  variant={session.status === 'active' ? 'default' : 'secondary'}
                  className="text-xs"
                >
                  {session.status}
                </Badge>
                {session.settings.model && (
                  <Badge variant="outline" className="text-xs">
                    {session.settings.model}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {messages.length} messages
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onSettingsClick && (
              <Button variant="ghost" size="icon" onClick={onSettingsClick}>
                <Settings className="h-4 w-4" />
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('markdown')}>
                  <Download className="h-4 w-4 mr-2" />
                  Export as Markdown
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('json')}>
                  <Download className="h-4 w-4 mr-2" />
                  Export as JSON
                </DropdownMenuItem>
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={onDelete}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Session
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>

      {/* Messages */}
      <CardContent className="flex-1 overflow-y-auto p-0">
        <MessageList
          messages={messages}
          streamingContent={streamContent}
          isStreaming={isStreaming}
        />
      </CardContent>

      {/* Input */}
      <div className="border-t p-4 shrink-0">
        {streamError && (
          <div className="mb-3 p-2 bg-destructive/10 text-destructive text-sm rounded-md flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {streamError}
          </div>
        )}

        <MessageInput
          onSend={handleSend}
          onStop={stopStream}
          isLoading={sendMessage.isPending}
          isStreaming={isStreaming}
          disabled={session.status !== 'active'}
          placeholder={
            session.status !== 'active'
              ? 'Session is not active'
              : 'Ask a question or describe what you want to build...'
          }
        />
      </div>
    </Card>
  );
}
